// src/components/dashboard/useDashboardLayout.js
// Per-user chart-layout prefs, global across analyses. One shared backend
// object { w1..w5 } (query key ['dashboard-layout']): single deduped GET,
// per-instance whole?.[workshopKey]?.lg slice, debounced (~400ms) PUT of the
// merged whole. Viewer/unauthenticated sessions are memory-only.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import dashboardLayoutService from '../../services/dashboardLayoutService';
import { CARD_MIN, COMPACT_DEFAULT_H, LEGACY_DEFAULT_H } from './defaultLayouts';
const WRITE_DEBOUNCE_MS = 400;
const COLS = 12;
let cacheWhole = null;
let cacheUserId = null;
let cachePromise = null;
const listeners = new Set();
const notify = (whole) => { listeners.forEach((fn) => fn(whole)); };
const fetchWhole = (userId) => {
  if (cachePromise && cacheUserId === userId) return cachePromise;
  cacheUserId = userId;
  cachePromise = dashboardLayoutService.getDashboardLayout().then((res) => {
    const data = res && res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : {};
    cacheWhole = data;
    notify(data);
    return data;
  }, () => { cachePromise = null; return null; });
  return cachePromise;
};
const isEmptySlice = (s) => (!s || typeof s !== 'object' || Array.isArray(s) || Object.keys(s).length === 0);
const cardMin = (id) => (id && CARD_MIN[id]) || { minW: 2, minH: 2 };
const clampEntry = (entry, fallback, id) => {
  const base = entry && typeof entry === 'object' ? entry : {};
  const fb = fallback && typeof fallback === 'object' ? fallback : {};
  const { minW, minH } = cardMin(id || base.i);
  const pick = (f, d) => (Number.isFinite(base[f]) ? base[f] : d);
  const w = Math.max(minW, Math.min(pick('w', Number.isFinite(fb.w) ? fb.w : 6), COLS));
  const clean = {
    x: Math.max(0, Math.min(pick('x', Number.isFinite(fb.x) ? fb.x : 0), COLS - w)),
    y: Math.max(0, pick('y', Number.isFinite(fb.y) ? fb.y : 0)),
    w, h: Math.max(minH, pick('h', Number.isFinite(fb.h) ? fb.h : 6))
  };
  if (base.hidden === true) clean.hidden = true;
  return clean;
};
const cleanSlice = (raw, defaults) => {
  const clean = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    Object.keys(raw).forEach((id) => {
      const e = raw[id];
      if (e && typeof e === 'object' && Number.isFinite(e.x) && Number.isFinite(e.y) && Number.isFinite(e.w) && Number.isFinite(e.h)) clean[id] = clampEntry(e, defaults[id], id);
    });
  }
  Object.keys(clean).forEach((id) => {
    const legacyH = LEGACY_DEFAULT_H[id];
    if (!Number.isFinite(legacyH)) return;
    const entry = clean[id];
    const fb = defaults && defaults[id];
    if (!fb || typeof fb !== 'object') return;
    if (!Number.isFinite(fb.h) || !Number.isFinite(fb.y)) return;
    const compactH = COMPACT_DEFAULT_H[id];
    const known = entry.h === legacyH || entry.h === compactH;
    if (!known) return;
    if (entry.x !== fb.x || entry.w !== fb.w) return;
    if (entry.h === fb.h) return;
    clean[id] = { ...entry, h: fb.h, y: fb.y };
  });
  return clean;
};
const useDashboardLayout = (analysisId, workshopKey, defaultLayout = {}) => {
  void analysisId;
  const { user, isAuthenticated } = useAuth();
  const userId = (user && (user._id || user.id)) || null;
  const canPersist = !!isAuthenticated && !!user && !!userId;
  const [whole, setWhole] = useState(cacheWhole);
  const [local, setLocal] = useState(null);
  const defaultsRef = useRef(defaultLayout);
  defaultsRef.current = defaultLayout;
  const persistRef = useRef(canPersist);
  persistRef.current = canPersist;
  const keyRef = useRef(workshopKey);
  keyRef.current = workshopKey;
  const timer = useRef(null);
  useEffect(() => {
    if (!canPersist) { setWhole(null); setLocal(null); return undefined; }
    if (cacheUserId !== userId) { cacheWhole = null; cachePromise = null; setWhole(null); setLocal(null); }
    else if (cacheWhole) setWhole(cacheWhole);
    const listener = (next) => setWhole(next);
    listeners.add(listener);
    fetchWhole(userId).then((data) => { if (data) setWhole(data); });
    return () => { listeners.delete(listener); if (timer.current) clearTimeout(timer.current); };
  }, [canPersist, userId]);
  const backendSlice = useMemo(() => cleanSlice(whole && whole[workshopKey] && whole[workshopKey].lg, defaultLayout), [whole, workshopKey, defaultLayout]);
  const stored = local || backendSlice;
  const storedRef = useRef(stored);
  storedRef.current = stored;
  const putSlice = useCallback((slice, force) => {
    if (!persistRef.current) return;
    const clean = cleanSlice(slice, defaultsRef.current);
    if (!force && isEmptySlice(clean)) return; // anti-clobber: never PUT empty over non-empty cache
    const next = { ...(cacheWhole && typeof cacheWhole === 'object' ? cacheWhole : {}) };
    next[keyRef.current] = { lg: clean };
    cacheWhole = next;
    notify(next);
    dashboardLayoutService.saveDashboardLayout(next).catch(() => {});
  }, []);
  const schedulePut = useCallback((slice) => {
    if (timer.current) clearTimeout(timer.current);
    const snapshot = { ...slice };
    timer.current = setTimeout(() => putSlice(snapshot, false), WRITE_DEBOUNCE_MS);
  }, [putSlice]);
  const applyLocal = useCallback((next) => { setLocal(next); schedulePut(next); }, [schedulePut]);
  const handleLayoutChange = useCallback((layout) => {
    if (!Array.isArray(layout)) return;
    const base = storedRef.current || {};
    const defaults = defaultsRef.current;
    const next = { ...base };
    let changed = false;
    layout.forEach((entry) => {
      if (!entry || typeof entry.i !== 'string') return;
      const candidate = clampEntry(entry, defaults[entry.i]);
      if (base[entry.i] && base[entry.i].hidden === true) candidate.hidden = true;
      const current = next[entry.i];
      const normalized = current && typeof current === 'object' ? { ...current } : current;
      if (normalized && normalized.hidden !== true) delete normalized.hidden;
      if (JSON.stringify(candidate) !== JSON.stringify(normalized)) changed = true;
      next[entry.i] = candidate;
    });
    if (!changed) return;
    applyLocal(next);
  }, [applyLocal]);
  const toggleHidden = useCallback((cardId) => {
    const base = storedRef.current || {};
    const current = base[cardId] ? base[cardId].hidden === true : false;
    applyLocal({ ...base, [cardId]: { ...clampEntry(base[cardId], defaultsRef.current[cardId], cardId), hidden: !current } });
  }, [applyLocal]);
  const showAll = useCallback(() => {
    const base = storedRef.current || {};
    if (!Object.keys(base).some((id) => base[id] && base[id].hidden === true)) return;
    const next = {};
    Object.keys(base).forEach((id) => { next[id] = { ...base[id], hidden: false }; });
    applyLocal(next);
  }, [applyLocal]);
  const resetLayout = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setLocal({});
    putSlice({}, true);
  }, [putSlice]);
  const hiddenCount = useMemo(() => Object.keys(stored).filter((id) => stored[id] && stored[id].hidden === true).length, [stored]);
  const layouts = useMemo(() => {
    const ids = new Set([...Object.keys(defaultLayout || {}), ...Object.keys(stored || {})]);
    return Array.from(ids).map((id) => ({ i: id, ...clampEntry((stored || {})[id], (defaultLayout || {})[id], id), ...cardMin(id), maxW: COLS }));
  }, [stored, defaultLayout]);
  return [layouts, handleLayoutChange, resetLayout, toggleHidden, showAll, hiddenCount];
};
export default useDashboardLayout;
